import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';

import Loader from '../../common/Loader';
import BackButton from '../../common/BackButton';
import Popup from '../../common/Popup';
import Checkbox from '../../common/Checkbox';
import PaymentAccountFormLoader from '../../common/PaymentAccountFormLoader';
import { COLORS } from '../../../constants/styles';
import Icon from '../../Icon';
import Button from '../../Button';
import { viewProgramCost } from '../utils/viewProgramCost';
import RadioGroup from '../../common/RadioGroup';
import { paymentInfoModalActions } from '../../../redux/programs/payment-info-modal';
import { consts as popupConsts } from '../../../redux/ui/popup';
import { UI_POPUP_TYPES } from '../../../constants/props';
import useDefaultCard from '../../../hooks/payments/account-form/useDefaultCard';
import { actions as accountVaultsFormActions } from '../../../redux/programs/accountVaultsForm';
import { actions as submitPaymentProgram } from '../../../redux/programs/submitPaymentProgram';
import {
  selectPaymentInfoModalOpen,
  selectAccountVaultsListLoading,
  selectPaymentInfoModalSubscriptionsSetting,
  selectPaymentInfoModalProgramId,
  selectPaymentInfoModalUserId,
  selectPaymentInfoModalWaitingPayaResponse,
  selectPaymentInfoModalMainUserId,
  selectPaymentInfoModalPrepayFlow,
  selectPaymentInfoModalDuePaymentDate,
} from '../../../redux/programs/selectors';
import { MEMBERSHIP_TYPE } from '../../../constants/programs';
import { SUBSCRIBABLE_TYPE, TRANSACTION_TYPE } from '../../../constants';

import styles from './styles.styl';

const MembershipPaymentInfoPopup = ({ intl: { formatMessage } }) => {
  const dispatch = useDispatch();
  const waitingPayaPaymentResponse = useSelector(
    selectPaymentInfoModalWaitingPayaResponse,
  );
  const isOpened = useSelector(selectPaymentInfoModalOpen);
  const programId = useSelector(selectPaymentInfoModalProgramId);
  const userId = useSelector(selectPaymentInfoModalUserId);
  const mainUserId = useSelector(selectPaymentInfoModalMainUserId);
  const loadingCards = useSelector(selectAccountVaultsListLoading);
  const subscriptionsSetting = useSelector(
    selectPaymentInfoModalSubscriptionsSetting,
  );
  const prepayFlow = useSelector(selectPaymentInfoModalPrepayFlow);
  const duePaymentDate = useSelector(selectPaymentInfoModalDuePaymentDate);

  const {
    price,
    period,
    periodQuantity,
    recurrenceType,
  } = subscriptionsSetting;

  const isOneTimePurchase =
    recurrenceType === MEMBERSHIP_TYPE.ONE_TIME_PURCHASE;

  const amount = viewProgramCost({
    price,
    period,
    recurrenceType,
    quantity: periodQuantity,
    formatMessage,
  });

  const { defaultCard, mappedCards } = useDefaultCard();

  const onClose = () => {
    dispatch(paymentInfoModalActions.close());
  };

  const [cardAgreement, setCardAgreement] = useState(false);
  const [automaticallyRenew, setAutomaticallyRenew] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState();

  const startDate = moment(prepayFlow ? duePaymentDate : new Date()).format(
    'll',
  );

  useEffect(() => {
    if (defaultCard) {
      setSelectedCardId(defaultCard.id);
    }
  }, [defaultCard]);

  const labelStyle = {
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: 'normal',
    opacity: 1,
    color: COLORS.fontRegular,
  };

  const handleOnChange = id => setSelectedCardId(id);

  const handleAddNewCard = () => {
    dispatch(accountVaultsFormActions.send({ userId: mainUserId, programId }));
    dispatch({
      type: popupConsts.SHOW,
      payload: {
        contentType: UI_POPUP_TYPES.PAYA_ACCOUNT_FORM,
        contentOptions: {
          noCards: false,
        },
      },
    });
  };

  const handleSubmitPayment = () => {
    dispatch(paymentInfoModalActions.waitingPayaPaymentResponse());
    dispatch(
      submitPaymentProgram.send({
        transaction: {
          userId,
          product: TRANSACTION_TYPE.USER_SUBSCRIPTION,
          subscribableId: programId,
          accountvaultId: selectedCardId,
          subscribableType: SUBSCRIBABLE_TYPE.PROGRAM,
          subscriptionsSettingId: subscriptionsSetting.id,
          ...(!isOneTimePurchase &&
            !prepayFlow && { autoRenewal: automaticallyRenew }),
        },
      }),
    );
  };

  return (
    <Popup opened={isOpened} withHeading={false}>
      <div className={styles.root}>
        <BackButton
          onBack={onClose}
          className={styles.backButton}
          disabled={waitingPayaPaymentResponse}
        />
        <div
          className={cn(styles.content, {
            [styles.loading]: waitingPayaPaymentResponse,
          })}
        >
          <div className={styles.title}>
            {formatMessage({ id: 'appointment.list.card.payment' })}
          </div>
          <div className={styles.description}>
            <div>
              <div className={styles.cellTitle}>
                {formatMessage({ id: 'paya.payment.amount' })}
              </div>
              <div className={styles.cellText}>{amount}</div>
            </div>
            <div>
              <div className={styles.cellTitle}>
                {formatMessage({ id: 'paya.payment.startDate' })}
              </div>
              <div className={styles.cellText}>{startDate}</div>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.title}>
              {formatMessage({ id: 'paya.payment.card' })}
            </div>
            {loadingCards && !mappedCards.length ? (
              <Loader />
            ) : (
              <RadioGroup
                name="cardsradioBtn"
                value={selectedCardId}
                onChange={handleOnChange}
                radios={mappedCards}
              />
            )}
            <div className={styles.btnAdd}>
              <button type="button" onClick={handleAddNewCard}>
                <Icon
                  name="add-text"
                  size={16}
                  style={{ fill: COLORS.bluePrimary }}
                />
                <span>
                  {formatMessage({ id: 'paya.payment.addNewPaymentMethod' })}
                </span>
              </button>
            </div>
            <Checkbox
              size={16}
              id="cardAgreement"
              name="cardAgreement"
              checked={cardAgreement}
              labelStyle={labelStyle}
              label={formatMessage({ id: 'paya.payment.cardAgreement' })}
              onChange={() => setCardAgreement(prev => !prev)}
            />
            {!isOneTimePurchase && !prepayFlow && (
              <Checkbox
                size={16}
                id="automaticallyRenew"
                name="automaticallyRenew"
                checked={automaticallyRenew}
                labelStyle={labelStyle}
                label={formatMessage({ id: 'paya.payment.automaticallyRenew' })}
                onChange={() => setAutomaticallyRenew(prev => !prev)}
              />
            )}
            <div className={styles.transactionDescription}>
              {formatMessage({ id: 'paya.payment.transactionDescription' })}
            </div>
          </div>
          <div className={styles.btnSubmit}>
            <Button
              disabled={
                cardAgreement === false ||
                !mappedCards.length ||
                waitingPayaPaymentResponse
              }
              color="primary"
              onClick={handleSubmitPayment}
            >
              {formatMessage({ id: 'paya.payment.submitPayment' })}
            </Button>
          </div>
        </div>
      </div>
      {waitingPayaPaymentResponse && (
        <div className={styles.loaderWrapper}>
          <PaymentAccountFormLoader />
        </div>
      )}
    </Popup>
  );
};

MembershipPaymentInfoPopup.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(MembershipPaymentInfoPopup);



// WEBPACK FOOTER //
// ./src/components/Programs/MembershipPaymentInfoPopup/index.jsx